import { UserManager, WebStorageStateStore, Log } from 'oidc-client';
import { setAuthenticationData } from 'redux/modules/fetch';
import { register } from '../../../redux/modules/user/userpreferences';
import { accessTokenIsPresent } from '../helpers';
import { browserHistory } from 'react-router';

const config = require('../../../../../config/main');
declare var window: any;

class AuthService {
	private UserManager;
	private accessToken;

	private baseUrlClient: string = config.app.baseUrlClient;
	private baseUrlIdentity: string = config.app.authSettings.baseUrlIdentity;
	private clientId: string = config.app.authSettings.clientId;
	private dispatch: any;

	constructor() {
		const IDENTITY_CONFIG = {
			authority: this.baseUrlIdentity,
			client_id: this.clientId,
			redirect_uri: this.baseUrlClient + '/signin-oidc',
			silent_redirect_uri: this.baseUrlClient + '/silentrenew',
			post_logout_redirect_uri: this.baseUrlClient + '/logout/callback',
			response_type: 'code',
			scope: 'openid profile email GlossiqApi',
			automaticSilentRenew: true,
			loadUserInfo: false,
		};

		const METADATA_OIDC = {
			issuer: this.baseUrlIdentity,
			jwks_uri: this.baseUrlIdentity + '/.well-known/openid-configuration/jwks',
			authorization_endpoint: this.baseUrlIdentity + '/connect/authorize',
			token_endpoint: this.baseUrlIdentity + '/connect/token',
			userinfo_endpoint: this.baseUrlIdentity + '/connect/userinfo',
			end_session_endpoint: this.baseUrlIdentity + '/connect/endsession',
			check_session_iframe: this.baseUrlIdentity + '/connect/checksession',
			revocation_endpoint: this.baseUrlIdentity + '/connect/revocation',
			introspection_endpoint: this.baseUrlIdentity + '/connect/introspect',
		};

		this.UserManager = new UserManager({
			...IDENTITY_CONFIG,
			userStore: new WebStorageStateStore({ store: window.localStorage }),
			metadata: {
				...METADATA_OIDC,
			},
		});

		// Logger
		Log.logger = console;
		Log.level = Log.DEBUG;

		this.UserManager.events.addUserLoaded((user) => {
			this.accessToken = user.access_token;
			localStorage.setItem('access_token', user.access_token);
			localStorage.setItem('id_token', user.id_token);
			this.setUserInfo({
				accessToken: this.accessToken,
				idToken: user.id_token,
			});
			if (this.accessToken) {
				this.dispatch(setAuthenticationData(this.accessToken));
			}

			this.dispatch(register(this.baseUrlClient)).then(() => {
				if (this.isLoginCallback()) {
					//isn't this always?why is this if needed?
					this.navigateToScreen();
				}
			});
		});

		this.UserManager.events.addSilentRenewError((e) => {
			console.error('silent renew error', e.message);
		});

		this.UserManager.events.addAccessTokenExpired(() => {
			console.info('token expired');
			this.signinSilent();
		});
	}

	public setFetchDefault(dispatch: any) {
		this.dispatch = dispatch;
	}

	public signinRedirectCallback = () => {
		this.UserManager.signinRedirectCallback().catch(() => {
			browserHistory.push('/');
		});
	};

	public getUser = async () => {
		const user = await this.UserManager.getUser();
		if (!user) {
			return await this.UserManager.signinRedirectCallback();
		}
		return user;
	};

	public parseJwt = (token) => {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace('-', '+').replace('_', '/');
		return JSON.parse(window.atob(base64));
	};

	public setUserInfo = (authResult) => {
		const data = this.parseJwt(this.accessToken);

		this.setSessionInfo(authResult);
		this.setUser(data);
	};

	public signinRedirect = (returnUrl?: string) => {
		if (window.location.pathname.indexOf('/admin/') !== -1) {
			localStorage.setItem('redirectUri', '/');
		} else {
			if (returnUrl) {
				// navigate to return url
				localStorage.setItem(
					'redirectUri',
					this.baseUrlClient + '/loginfinished?' + this.getRedirectUrlSearchParams(returnUrl),
				);
			} else {
				// navigate to current location
				localStorage.setItem(
					'redirectUri',
					this.baseUrlClient + '/loginfinished?' + this.getRedirectUrlSearchParams(undefined, true),
				);
			}
		}

		this.UserManager.signinRedirect({});
	};

	public signinRedirectJoin = () => {
		localStorage.setItem(
			'redirectUri',
			this.baseUrlClient + '/loginfinished?' + this.getRedirectUrlSearchParams(undefined, true),
		);
		this.UserManager.signinRedirect({ extraQueryParams: { IsRegister: true } });
	};

	public setUser = (data) => {
		localStorage.setItem('userId', data.sub);
	};

	public navigateToScreen = () => {
		const redirectUri = localStorage.getItem('redirectUri') ? localStorage.getItem('redirectUri') : '/';

		window.location.replace(redirectUri);
	};

	public setSessionInfo(authResult) {
		localStorage.setItem('access_token', authResult.accessToken);
		localStorage.setItem('id_token', authResult.idToken);
	}

	public isAuthenticated = () => {
		return accessTokenIsPresent();
	};

	public signinSilent = () => {
		this.UserManager.signinSilent()
			.then((user) => {
				console.info('signed in', user);
			})
			.catch((err) => {
				console.error(err);
				//TODO: handle silent sign in
				this.signinRedirect();
			});
	};

	public signinSilentCallback = () => {
		this.UserManager.signinSilentCallback();
	};

	public createSigninRequest = () => {
		return this.UserManager.createSigninRequest();
	};

	public logout = () => {
		this.UserManager.signoutRedirect({
			id_token_hint: localStorage.getItem('id_token'),
		});
		this.UserManager.clearStaleState();
	};

	public logoutDeactivatedUser = () => {
		this.UserManager.signoutRedirect({
			id_token_hint: localStorage.getItem('id_token'),
			state: 'deactivateuser',
		});
		this.UserManager.clearStaleState();
	};

	public signoutRedirectCallback = () => {
		this.UserManager.signoutRedirectCallback().then((data: any) => {
			let url: string = this.baseUrlClient + '/home';

			if (data && data.state && data.state === 'deactivateuser') {
				url = url + '?isDeactivateUser=true';
			}

			localStorage.clear();
			window.location.replace(url);
		});
		this.UserManager.clearStaleState();
	};

	private isLoginCallback(): boolean {
		return window.location.href.indexOf('signin-oidc') !== -1;
	}

	private getRedirectUrlSearchParams(redirectUrl?: string, current?: boolean) {
		const urlParams = {};
		if (redirectUrl) {
			urlParams['returnUrl'] = redirectUrl;
		} else if (current && window.location.pathname.substr(1)?.length) {
			urlParams['returnUrl'] = window.location.pathname.substr(1) + window.location.search;
		}
		return new URLSearchParams(urlParams).toString();
	}
}

export { AuthService };
